import React from 'react';
import { bool, func, node, object, string } from 'prop-types';
import classNames from 'classnames';
import NamedLink from '../NamedLink/NamedLink';
import css from './MenuAction.module.css';

/**
 * One row of a menu, from the Components board: an optional icon, the label, a hover fill, and a
 * red `destructive` tone for leaving or deleting. A button by default (its click doesn't reach a
 * table row behind it); with `linkProps` ({ name, params, to }) it is a link to a page.
 * `iconSpace` keeps an empty icon column on a row without an icon, so its label lines up with the
 * labels of the rows that have one. Render it inside a <MenuItem>.
 */
const MenuAction = props => {
  const {
    rootClassName,
    className,
    icon,
    iconSpace,
    destructive,
    linkProps,
    onClick,
    disabled,
    children,
  } = props;
  const classes = classNames(rootClassName || css.root, className, {
    [css.destructive]: destructive,
  });
  const content = (
    <>
      {icon ? (
        <span className={css.icon} aria-hidden="true">
          {icon}
        </span>
      ) : iconSpace ? (
        <span className={css.iconSpace} aria-hidden="true" />
      ) : null}
      {children}
    </>
  );

  if (linkProps) {
    return (
      <NamedLink {...linkProps} className={classes} onClick={onClick || undefined}>
        {content}
      </NamedLink>
    );
  }
  return (
    <button
      type="button"
      className={classes}
      disabled={disabled}
      onClick={e => {
        e.stopPropagation();
        if (onClick) onClick(e);
      }}
    >
      {content}
    </button>
  );
};

MenuAction.defaultProps = {
  rootClassName: null,
  className: null,
  icon: null,
  iconSpace: false,
  destructive: false,
  linkProps: null,
  onClick: null,
  disabled: false,
};

MenuAction.propTypes = {
  rootClassName: string,
  className: string,
  icon: node,
  iconSpace: bool,
  destructive: bool,
  linkProps: object,
  onClick: func,
  disabled: bool,
  children: node.isRequired,
};

export default MenuAction;
